import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { trackPageViewed, trackCardOpened } from '../utils/analytics';
import { Assignment, UnreviewedCard, RecommendedCard } from '../types/api';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';
import { CardDetail } from './CardDetail';
import { PracticeCard } from './PracticeCard';
import { CardPracticePickerModal } from './CardPracticePickerModal';
import { GeneratePhrasesModal } from './GeneratePhrasesModal';
import { GenerateCollocationsModal } from './GenerateCollocationsModal';
import { GenerateSynonymsModal } from './GenerateSynonymsModal';
import { GenerateAntonymsModal } from './GenerateAntonymsModal';
import { GenerateImageModal } from './GenerateImageModal';
import { AssignmentProgressModal } from './AssignmentProgressModal';
import { CategoryPracticeSession } from './CategoryPracticeSession';
import { InsightsRow } from './InsightsRow';
import { GoalsSection } from './GoalsSection';
import { Loader2, Folder, ArrowRight, ClipboardList, EyeOff, Zap, Plus, BookOpen, Sparkles, FolderPlus, Brain, ChevronRight } from 'lucide-react';
import { buildTree, flattenTree } from '../utils/treeUtils';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedInstanceId, instancesWithRoles, instances } = useInstance();

  useEffect(() => { trackPageViewed('dashboard'); }, []);
  const { user } = useAuth();

  const isInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);

  const [moveCategories, setMoveCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [viewingCard, setViewingCard] = useState<{ id: number; name?: string } | null>(null);
  const [practiceCard, setPracticeCard] = useState<{ cardId: number; cardName: string } | null>(null);
  const [showCardPracticePicker, setShowCardPracticePicker] = useState(false);
  const [showSpotlightFlashcard, setShowSpotlightFlashcard] = useState(false);
  const [showSpotlightPhrases, setShowSpotlightPhrases] = useState(false);
  const [showSpotlightCollocations, setShowSpotlightCollocations] = useState(false);
  const [showSpotlightSynonyms, setShowSpotlightSynonyms] = useState(false);
  const [showSpotlightAntonyms, setShowSpotlightAntonyms] = useState(false);
  const [showSpotlightImage, setShowSpotlightImage] = useState(false);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [progressAssignment, setProgressAssignment] = useState<Assignment | null>(null);
  const [folderNames, setFolderNames] = useState<Record<number, string>>({});
  const [categoryNames, setCategoryNames] = useState<Record<number, string>>({});
  const [practiceAssignment, setPracticeAssignment] = useState<Assignment | null>(null);
  const [categoryPracticeCards, setCategoryPracticeCards] = useState<{ id: number; name: string }[]>([]);
  const [unreviewedCards, setUnreviewedCards] = useState<UnreviewedCard[]>([]);
  const [unreviewedTotal, setUnreviewedTotal] = useState(0);
  const [spotlightIndex, setSpotlightIndex] = useState(0);

  const [recommendedCards, setRecommendedCards] = useState<RecommendedCard[]>([]);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(true);
  const [quickAddWord, setQuickAddWord] = useState('');
  const [quickAddCategoryId, setQuickAddCategoryId] = useState<number | ''>('');
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [quickAddSuccess, setQuickAddSuccess] = useState(false);
  const quickAddSuccessTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isCategoriesLoaded, setIsCategoriesLoaded] = useState(false);

  useEffect(() => {
    if (!selectedInstanceId) { setMoveCategories([]); return; }
    const loadCategories = async () => {
      try {
        const items = await apiClient.getCategories(selectedInstanceId);
        const cats = isInstanceOwner ? items.map((c) => ({ id: c.id, name: c.name })) : [];
        setMoveCategories(cats);
        if (cats.length > 0 && quickAddCategoryId === '') setQuickAddCategoryId(cats[0].id);
      } catch { setMoveCategories([]); } finally { setIsCategoriesLoaded(true); }
    };
    loadCategories();
  }, [selectedInstanceId, isInstanceOwner]);

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        const data = await apiClient.getAssignments();
        setAssignments(data.items);
        const cardIds = data.items.map((a) => a.card_id).filter((id): id is number => id !== null).filter((id, i, arr) => arr.indexOf(id) === i);
        const catIds = data.items.map((a) => a.category_id).filter((id): id is number => id !== null).filter((id, i, arr) => arr.indexOf(id) === i);
        const folderNameMap: Record<number, string> = {};
        await Promise.all([
          Promise.all(cardIds.map(async (id) => {
            try { const card = await apiClient.getCard(id); folderNameMap[id] = card.name; }
            catch { folderNameMap[id] = `Folder #${id}`; }
          })),
          (async () => {
            if (catIds.length === 0) return;
            try { const cats = await apiClient.getCategories(); const m: Record<number, string> = {}; cats.forEach((c) => { m[c.id] = c.name; }); setCategoryNames(m); }
            catch {}
          })(),
        ]);
        setFolderNames(folderNameMap);
      } catch {}
    };
    loadAssignments();
  }, []);


  useEffect(() => {
    const load = async () => {
      try { const d = await apiClient.getUnreviewedCards(selectedInstanceId ?? undefined); setUnreviewedCards(d.items); setUnreviewedTotal(d.total); }
      catch {}
    };
    load();
  }, [selectedInstanceId]);

  useEffect(() => {
    const load = async () => {
      setIsLoadingRecommended(true);
      try {
        const data = await apiClient.getRecommendedCards(selectedInstanceId ?? undefined);
        setRecommendedCards(data.items);
      } catch {} finally { setIsLoadingRecommended(false); }
    };
    load();
  }, [selectedInstanceId]);

  useEffect(() => {
    return () => { if (quickAddSuccessTimeout.current) clearTimeout(quickAddSuccessTimeout.current); };
  }, []);

  const handlePracticeAssignment = async (assignment: Assignment) => {
    try {
      let leafCards: { id: number; name: string }[] = [];
      if (assignment.card_id !== null) {
        const response = await apiClient.getCardsByParent(assignment.card_id, { instance_id: assignment.instance_id });
        leafCards = response.items.filter((c) => !c.is_folder).map((c) => ({ id: c.id, name: c.name }));
      } else if (assignment.category_id !== null) {
        const cards = await apiClient.getCardsHierarchy(assignment.category_id, assignment.instance_id);
        const treeItems = buildTree(cards, new Set());
        leafCards = flattenTree(treeItems).filter((c) => !c.is_folder).map((c) => ({ id: c.id, name: c.name }));
      }
      setCategoryPracticeCards(leafCards);
      setPracticeAssignment(assignment);
    } catch {}
  };

  const resolveAssignmentName = (assignment: Assignment): string => {
    if (assignment.card_id !== null) return folderNames[assignment.card_id] ?? `Folder #${assignment.card_id}`;
    if (assignment.category_id !== null) return categoryNames[assignment.category_id] ?? `Category #${assignment.category_id}`;
    return 'Unknown';
  };

  const handleCardDelete = useCallback((_cardId: number) => {}, []);

  const handleCardMove = useCallback(
    async (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => {
      await apiClient.moveCard(itemId, data);
    }, []
  );


  const handleQuickAdd = async () => {
    if (!quickAddWord.trim() || quickAddCategoryId === '' || isQuickAdding) return;
    setIsQuickAdding(true);
    try {
      await apiClient.createCard({ name: quickAddWord.trim(), category_id: quickAddCategoryId as number });
      setQuickAddWord('');
      setQuickAddSuccess(true);
      if (quickAddSuccessTimeout.current) clearTimeout(quickAddSuccessTimeout.current);
      quickAddSuccessTimeout.current = setTimeout(() => setQuickAddSuccess(false), 2500);
    } catch {} finally { setIsQuickAdding(false); }
  };

  const spotlightCard = unreviewedCards[spotlightIndex] ?? null;
  const collectionName = instances.find(i => i.id === selectedInstanceId)?.name ?? null;
  const recommendedCount = recommendedCards.length;
  const assignedToMe = assignments.filter((a) => a.created_by !== user?.id);

  const isLoading = isLoadingRecommended || !isCategoriesLoaded;

  const isEmpty =
    !isLoadingRecommended &&
    isCategoriesLoaded &&
    recommendedCount === 0 &&
    unreviewedCards.length === 0 &&
    moveCategories.length === 0 &&
    assignedToMe.length === 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{collectionName ?? 'Dashboard'}</h1>
          {collectionName && <p className="text-xs text-gray-400 mt-0.5">Dashboard</p>}
        </div>
        <Link
          to="/tree"
          className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex-shrink-0"
        >
          Library
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Loading your dashboard...</p>
        </div>
      ) : isEmpty ? (
        /* ── Onboarding empty state ── */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 px-6 py-8 text-white text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-4">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-xl font-bold mb-1">
              Welcome{user?.first_name ? `, ${user.first_name}` : ''}!
            </h2>
            <p className="text-sm text-purple-100">
              Your vocabulary library is empty. Let's get you started.
            </p>
          </div>
          <div className="px-6 py-6 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">3 steps to your first practice session</p>
            <div className="space-y-3">
              {[
                { icon: FolderPlus, label: 'Create a category', sub: 'e.g. "Business English" or "Phrasal Verbs"', step: '1', linked: true },
                { icon: Plus,       label: 'Add words to it',   sub: 'Type or use AI to build your list',       step: '2', linked: true },
                { icon: Brain,      label: 'Start practicing',  sub: 'Phrases, flashcards, and more',           step: '3', linked: false },
              ].map(({ icon: Icon, label, sub, step, linked }) => {
                const inner = (
                  <>
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">
                      {step}
                    </div>
                    <div className="flex items-start gap-3 flex-1">
                      <Icon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{label}</p>
                        <p className="text-xs text-gray-400">{sub}</p>
                      </div>
                      {linked && <ChevronRight className="h-4 w-4 text-purple-400 mt-1 flex-shrink-0" />}
                    </div>
                  </>
                );
                return linked ? (
                  <Link key={step} to="/tree" className="flex items-start gap-3 rounded-lg hover:bg-purple-50 -mx-2 px-2 py-1 transition-colors">
                    {inner}
                  </Link>
                ) : (
                  <div key={step} className="flex items-start gap-3 px-2 py-1">
                    {inner}
                  </div>
                );
              })}
            </div>
            <Link
              to="/tree"
              className="mt-2 flex items-center justify-center gap-2 w-full py-3 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Go to Library
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      ) : (
        <>
      {/* Practice CTA */}
      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-green-100 mb-1">Ready to practice</p>
            {isLoadingRecommended ? (
              <div className="h-8 w-32 bg-white/20 rounded animate-pulse" />
            ) : (
              <p className="text-3xl font-bold">
                {recommendedCount}
                <span className="text-lg font-normal text-green-100 ml-2">
                  {recommendedCount === 1 ? 'card' : 'cards'}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/tree')}
            disabled={recommendedCount === 0}
            className="flex items-center gap-2 px-5 py-3 bg-white text-green-700 font-semibold rounded-xl hover:bg-green-50 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 text-sm"
          >
            <Zap className="h-4 w-4" />
            Practice Now
          </button>
        </div>
      </div>

      {/* Assigned to you */}
      {assignedToMe.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
              <ClipboardList className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Assigned to you</h2>
              <p className="text-xs text-gray-400">Folders your teacher assigned</p>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {assignedToMe.map((assignment) => (
              <div key={assignment.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Folder className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-800 truncate">{resolveAssignmentName(assignment)}</span>
                  {assignment.due_date && (
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      Due {new Date(assignment.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handlePracticeAssignment(assignment)}
                  className="ml-3 flex-shrink-0 px-3 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                >
                  Practice
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two-column: Spotlight + Quick Add */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Word Spotlight */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
              <EyeOff className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Word spotlight</h2>
              <p className="text-xs text-gray-400">
                {unreviewedTotal > 0 ? `${unreviewedTotal} never reviewed` : 'All caught up'}
              </p>
            </div>
          </div>
          <div className="p-4">
            {spotlightCard ? (
              <div className="space-y-3">
                <div
                  className="cursor-pointer"
                  onClick={() => { setViewingCard({ id: spotlightCard.id, name: spotlightCard.name }); trackCardOpened(spotlightCard.id); }}
                >
                  <p className="text-lg font-bold text-gray-900">{spotlightCard.name}</p>
                  {spotlightCard.category_name && (
                    <p className="text-xs text-gray-400 mt-0.5">{spotlightCard.category_name}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setPracticeCard({ cardId: spotlightCard.id, cardName: spotlightCard.name }); setShowCardPracticePicker(true); }}
                    className="flex-1 py-2 text-sm font-medium text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    Practice this word
                  </button>
                  {unreviewedCards.length > 1 && (
                    <button
                      onClick={() => setSpotlightIndex((i) => (i + 1) % unreviewedCards.length)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Show next word"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 text-gray-400">
                <BookOpen className="h-6 w-6 mb-2 opacity-40" />
                <p className="text-xs text-center">All cards reviewed at least once</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Add */}
        {isInstanceOwner && moveCategories.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Add a word</h2>
                <p className="text-xs text-gray-400">Quick-capture to your library</p>
              </div>
            </div>
            <form
              className="p-4 space-y-3"
              onSubmit={(e) => { e.preventDefault(); handleQuickAdd(); }}
            >
              <input
                type="text"
                value={quickAddWord}
                onChange={(e) => setQuickAddWord(e.target.value)}
                placeholder="e.g. ephemeral"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300"
              />
              <select
                value={quickAddCategoryId}
                onChange={(e) => setQuickAddCategoryId(Number(e.target.value))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 bg-white text-gray-700"
              >
                {moveCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!quickAddWord.trim() || quickAddCategoryId === '' || isQuickAdding}
                className="w-full py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isQuickAdding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : quickAddSuccess ? (
                  '✓ Added!'
                ) : (
                  <><Plus className="h-4 w-4" />Add word</>
                )}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Insights row */}
      <InsightsRow instanceId={selectedInstanceId} />

      {/* Daily goals */}
      <GoalsSection />
        </>
      )}

      {/* Modals */}
      {viewingCard !== null && (
        <CardDetail
          cardId={viewingCard.id}
          initialCardName={viewingCard.name}
          onClose={() => setViewingCard(null)}
          onSave={() => {}}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? handleCardDelete : undefined}
          onMove={isInstanceOwner ? handleCardMove : undefined}
          categories={moveCategories}
        />
      )}

      {showCardPracticePicker && practiceCard !== null && (
        <CardPracticePickerModal
          cardName={practiceCard.cardName}
          onClose={() => setShowCardPracticePicker(false)}
          onFlashcard={() => setShowSpotlightFlashcard(true)}
          onGeneratePhrases={() => setShowSpotlightPhrases(true)}
          onGenerateCollocations={() => setShowSpotlightCollocations(true)}
          onGenerateSynonyms={() => setShowSpotlightSynonyms(true)}
          onGenerateAntonyms={() => setShowSpotlightAntonyms(true)}
          onGenerateImage={() => setShowSpotlightImage(true)}
        />
      )}

      {showSpotlightFlashcard && practiceCard !== null && (
        <PracticeCard
          cardId={practiceCard.cardId}
          cardName={practiceCard.cardName}
          onClose={() => { setShowSpotlightFlashcard(false); setPracticeCard(null); }}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? handleCardDelete : undefined}
          onMove={isInstanceOwner ? handleCardMove : undefined}
          categories={moveCategories}
        />
      )}

      {showSpotlightPhrases && practiceCard !== null && (
        <GeneratePhrasesModal cardId={practiceCard.cardId} cardName={practiceCard.cardName} onClose={() => setShowSpotlightPhrases(false)} />
      )}
      {showSpotlightCollocations && practiceCard !== null && (
        <GenerateCollocationsModal cardId={practiceCard.cardId} cardName={practiceCard.cardName} onClose={() => setShowSpotlightCollocations(false)} />
      )}
      {showSpotlightSynonyms && practiceCard !== null && (
        <GenerateSynonymsModal cardId={practiceCard.cardId} cardName={practiceCard.cardName} onClose={() => setShowSpotlightSynonyms(false)} />
      )}
      {showSpotlightAntonyms && practiceCard !== null && (
        <GenerateAntonymsModal cardId={practiceCard.cardId} cardName={practiceCard.cardName} onClose={() => setShowSpotlightAntonyms(false)} />
      )}
      {showSpotlightImage && practiceCard !== null && (
        <GenerateImageModal cardId={practiceCard.cardId} cardName={practiceCard.cardName} onClose={() => setShowSpotlightImage(false)} onSaved={() => setShowSpotlightImage(false)} />
      )}

      {practiceAssignment !== null && (
        <CategoryPracticeSession
          name={resolveAssignmentName(practiceAssignment)}
          initialLeafCards={categoryPracticeCards}
          onClose={() => { setPracticeAssignment(null); setCategoryPracticeCards([]); }}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? handleCardDelete : undefined}
          onMove={isInstanceOwner ? handleCardMove : undefined}
          categories={moveCategories}
        />
      )}

      {progressAssignment !== null && (
        <AssignmentProgressModal
          assignment={progressAssignment}
          folderName={resolveAssignmentName(progressAssignment)}
          onClose={() => setProgressAssignment(null)}
        />
      )}
    </div>
  );
};
